import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { csvSafe } from "@/lib/format";
import { toast } from "sonner";
import { CreditCard, Save, Plus, Trash2, RefreshCcw, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PageHeader from "@/components/layout/PageHeader";

const METHODS = [
  { id: "mobile_money", label: "Mobile Money" },
  { id: "card", label: "Carte bancaire" },
  { id: "transfer", label: "Virement" },
  { id: "cash", label: "Espèces" },
  { id: "other", label: "Autre" },
];

const emptyPayment = { society_id: "", plan_id: "decouverte", duration_months: 1, amount_xof: 0, method: "mobile_money", reference: "", note: "" };

export default function DevPaiements() {
  const [tab, setTab] = useState("tarifs");
  const [priceGrid, setPriceGrid] = useState(null); // { plans, durations, prices, currency }
  const [savingPrices, setSavingPrices] = useState(false);
  const [payments, setPayments] = useState([]);
  const [societies, setSocieties] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(emptyPayment);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const loadAll = async () => {
    const [p, pay, socs] = await Promise.all([
      api.get("/dev/subscription-prices"),
      api.get("/dev/subscription-payments"),
      api.get("/societies"),
    ]);
    setPriceGrid(p.data);
    setPayments(pay.data);
    setSocieties(socs.data);
  };
  useEffect(() => { loadAll(); }, []);

  const totalXof = useMemo(() => payments.reduce((s, p) => s + (p.amount_xof || 0), 0), [payments]);
  const totalCount = payments.length;

  const savePrices = async () => {
    if (!priceGrid) return;
    setSavingPrices(true);
    try {
      await api.put("/dev/subscription-prices", { prices: priceGrid.prices });
      toast.success("Tarifs enregistrés");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Enregistrement échoué");
    } finally {
      setSavingPrices(false);
    }
  };

  const updatePrice = (planId, duration, val) => {
    const n = Math.max(0, parseInt(val || 0, 10) || 0);
    setPriceGrid((g) => ({ ...g, prices: { ...g.prices, [planId]: { ...g.prices[planId], [String(duration)]: n } } }));
  };

  const openAdd = () => { setForm(emptyPayment); setAddOpen(true); };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!form.society_id) { toast.error("Sélectionnez une société"); return; }
    if (!form.amount_xof || form.amount_xof <= 0) { toast.error("Montant invalide"); return; }
    setSaving(true);
    try {
      await api.post("/dev/subscription-payments", {
        society_id: form.society_id,
        plan_id: form.plan_id,
        duration_months: Number(form.duration_months),
        amount_xof: Number(form.amount_xof),
        method: form.method,
        reference: form.reference.trim(),
        note: form.note.trim(),
      });
      toast.success("Paiement enregistré — société mise à jour");
      setAddOpen(false);
      loadAll();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const deletePayment = async () => {
    try {
      await api.delete(`/dev/subscription-payments/${confirmDeleteId}`);
      toast.success("Paiement supprimé");
      setConfirmDeleteId(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
    }
  };

  // Suggest the price when the DEV picks a plan × duration in the "add payment" form.
  const suggested = priceGrid?.prices?.[form.plan_id]?.[String(form.duration_months)] || 0;
  useEffect(() => {
    if (addOpen) setForm((f) => ({ ...f, amount_xof: suggested }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.plan_id, form.duration_months, addOpen]);

  const exportCSV = () => {
    const lines = ["Date;Société;Plan;Durée;Montant XOF;Méthode;Référence;Note"];
    payments.forEach((p) => {
      lines.push([
        p.paid_at?.slice(0, 10) || "",
        csvSafe(p.society_nom || ""),
        csvSafe(p.plan_id),
        `${p.duration_months} mois`,
        p.amount_xof,
        csvSafe(p.method),
        csvSafe(p.reference || ""),
        csvSafe((p.note || "").replaceAll("\n", " ")),
      ].join(";"));
    });
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `paiements_abonnements_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<CreditCard className="w-6 h-6" />}
        title="Paiements & Abonnements"
        subtitle="Grille des tarifs et historique des paiements reçus des sociétés"
        actions={
          <Button variant="outline" onClick={loadAll} data-testid="btn-paiement-refresh">
            <RefreshCcw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="bg-slate-100" data-testid="tabs-paiement">
          <TabsTrigger value="tarifs" data-testid="tab-tarifs">Tarifs</TabsTrigger>
          <TabsTrigger value="paiements" data-testid="tab-paiements">Historique des paiements</TabsTrigger>
        </TabsList>

        {/* ==================== TARIFS ==================== */}
        <TabsContent value="tarifs" className="space-y-4">
          <Card className="p-6 border-slate-200/70 shadow-sm">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900">Grille des tarifs d'abonnement</h3>
                <p className="text-sm text-slate-500">
                  Prix par formule et par durée en <strong>{priceGrid?.currency || "XOF"}</strong>. Ces montants seront
                  proposés automatiquement lors de l'enregistrement d'un paiement.
                </p>
              </div>
              <Button onClick={savePrices} disabled={savingPrices || !priceGrid} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-save-prices">
                <Save className="w-4 h-4 mr-2" /> {savingPrices ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/60">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Formule</TableHead>
                    {(priceGrid?.durations || [1, 3, 6, 12]).map((d) => (
                      <TableHead key={d} className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">{d} mois</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(priceGrid?.plans || []).map((p) => (
                    <TableRow key={p.id} className="hover:bg-slate-50/60" data-testid={`row-price-${p.id}`}>
                      <TableCell className="font-medium text-slate-900">{p.label}</TableCell>
                      {priceGrid.durations.map((d) => (
                        <TableCell key={d} className="text-right">
                          <Input
                            type="number"
                            min="0"
                            step="500"
                            value={priceGrid.prices[p.id]?.[String(d)] ?? 0}
                            onChange={(e) => updatePrice(p.id, d, e.target.value)}
                            className="w-32 ml-auto text-right font-mono"
                            data-testid={`input-price-${p.id}-${d}`}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>

        {/* ==================== HISTORIQUE ==================== */}
        <TabsContent value="paiements" className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Card className="p-4 border-slate-200/70 shadow-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Nombre de paiements</div>
              <div className="text-2xl font-heading font-bold text-slate-900 mt-1" data-testid="stat-payments-count">{totalCount}</div>
            </Card>
            <Card className="p-4 border-slate-200/70 shadow-sm">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total encaissé (XOF)</div>
              <div className="text-2xl font-heading font-bold text-emerald-700 mt-1" data-testid="stat-payments-total">{totalXof.toLocaleString("fr-FR")}</div>
            </Card>
            <Card className="p-4 border-slate-200/70 shadow-sm flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Actions</div>
                <div className="text-xs text-slate-500 mt-1">Enregistrer ou exporter</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={exportCSV} disabled={!payments.length} data-testid="btn-export-payments">
                  <FileDown className="w-4 h-4 mr-2" /> CSV
                </Button>
                <Button onClick={openAdd} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-add-payment">
                  <Plus className="w-4 h-4 mr-2" /> Nouveau
                </Button>
              </div>
            </Card>
          </div>

          <Card className="p-0 border-slate-200/70 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/60">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Société</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Formule</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Durée</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Montant</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Méthode</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Référence</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold w-20"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-slate-500 py-12" data-testid="payments-empty">
                        Aucun paiement enregistré pour le moment.
                      </TableCell>
                    </TableRow>
                  )}
                  {payments.map((p) => (
                    <TableRow key={p.id} className="hover:bg-slate-50/60" data-testid={`row-payment-${p.id}`}>
                      <TableCell className="text-slate-700 font-mono text-xs">{p.paid_at?.slice(0, 10)}</TableCell>
                      <TableCell className="font-medium text-slate-900">{p.society_nom || p.society_id.slice(0, 8)}</TableCell>
                      <TableCell className="text-slate-700">{p.plan_id.replace("_plus", "+").replace(/^./, (c) => c.toUpperCase())}</TableCell>
                      <TableCell className="text-slate-700">{p.duration_months} mois</TableCell>
                      <TableCell className="text-right font-mono font-semibold text-emerald-700">{p.amount_xof.toLocaleString("fr-FR")}</TableCell>
                      <TableCell className="text-slate-600">{METHODS.find((m) => m.id === p.method)?.label || p.method}</TableCell>
                      <TableCell className="text-slate-500 text-xs font-mono">{p.reference || "—"}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => setConfirmDeleteId(p.id)} className="hover:bg-rose-50 hover:text-rose-600" data-testid={`btn-del-payment-${p.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ============ Add payment dialog ============ */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg" data-testid="dialog-add-payment">
          <DialogHeader>
            <DialogTitle>Enregistrer un paiement d'abonnement</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitPayment} className="space-y-3">
            <div className="space-y-2">
              <Label>Société *</Label>
              <Select value={form.society_id} onValueChange={(v) => setForm({ ...form, society_id: v })}>
                <SelectTrigger data-testid="select-payment-society"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>
                  {societies.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`payment-society-${s.id}`}>{s.nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Formule *</Label>
                <Select value={form.plan_id} onValueChange={(v) => setForm({ ...form, plan_id: v })}>
                  <SelectTrigger data-testid="select-payment-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(priceGrid?.plans || []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Durée *</Label>
                <Select value={String(form.duration_months)} onValueChange={(v) => setForm({ ...form, duration_months: Number(v) })}>
                  <SelectTrigger data-testid="select-payment-duration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(priceGrid?.durations || [1, 3, 6, 12]).map((d) => (
                      <SelectItem key={d} value={String(d)}>{d} mois</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Montant (XOF) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="500"
                  value={form.amount_xof}
                  onChange={(e) => setForm({ ...form, amount_xof: e.target.value })}
                  required
                  data-testid="input-payment-amount"
                />
                {suggested > 0 && (
                  <p className="text-xs text-slate-500">Tarif suggéré : <strong>{suggested.toLocaleString("fr-FR")} XOF</strong></p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Méthode *</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="select-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {METHODS.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Référence (transaction, virement…)</Label>
              <Input
                value={form.reference}
                onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="TX-XXXX ou n° reçu…"
                data-testid="input-payment-reference"
              />
            </div>
            <div className="space-y-2">
              <Label>Note (optionnelle)</Label>
              <Textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={2}
                data-testid="input-payment-note"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)} data-testid="btn-payment-cancel">Annuler</Button>
              <Button type="submit" disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="btn-payment-save">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ============ Confirm delete ============ */}
      <Dialog open={!!confirmDeleteId} onOpenChange={(o) => !o && setConfirmDeleteId(null)}>
        <DialogContent data-testid="dialog-confirm-delete-payment">
          <DialogHeader>
            <DialogTitle className="text-rose-700">Supprimer ce paiement ?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-600">Cette action est irréversible. La société ne sera pas rétrogradée automatiquement.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDeleteId(null)}>Annuler</Button>
            <Button onClick={deletePayment} className="bg-rose-600 hover:bg-rose-700" data-testid="btn-confirm-delete-payment">Supprimer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
